/**
 * Insights/Analytics Service - Stripe-style dashboard analytics
 *
 * Features:
 * - KPI cards with trends
 * - Pipeline analytics
 * - Task completion metrics
 * - Team performance insights
 */

import type { Insight, InsightType, DashboardKPI, DashboardData, ActivityItem } from '../types';
import {
  getStore,
  addEntity,
  getEntity,
  getEntitiesByWorkspace,
  deleteEntity,
} from '../store';
import { generateId } from '../id';
import { getTasksByWorkspace, getOverdueTasks, getTasksDueToday, getTaskStats } from './tasks.service';
import { getContactsByWorkspace, getContactStats, getTotalDealValue, getCrmSummary } from './crm.service';
import { getNotesByWorkspace } from './notes.service';
import { getTeamStats } from './team.service';

export interface CreateInsightInput {
  workspaceId: string;
  type: InsightType;
  title: string;
  summary: string;
  data?: Record<string, unknown>;
  priority?: 'info' | 'action' | 'urgent';
  metric?: string;
  value?: number;
  change?: number;
  trend?: 'up' | 'down' | 'stable';
}

export function createInsight(input: CreateInsightInput): Insight {
  const now = new Date();
  const insight: Insight = {
    id: generateId('insight'),
    workspaceId: input.workspaceId,
    type: input.type,
    title: input.title,
    summary: input.summary,
    data: input.data ?? {},
    priority: input.priority ?? 'info',
    metric: input.metric,
    value: input.value,
    change: input.change,
    trend: input.trend,
    createdAt: now,
    updatedAt: now,
  };

  return addEntity(getStore().insights, insight);
}

export function getInsight(id: string): Insight | undefined {
  return getEntity(getStore().insights, id);
}

export function getInsightsByWorkspace(workspaceId: string): Insight[] {
  return getEntitiesByWorkspace(getStore().insights, workspaceId);
}

export function getInsightsByType(workspaceId: string, type: InsightType): Insight[] {
  return getInsightsByWorkspace(workspaceId).filter((i) => i.type === type);
}

export function deleteInsight(id: string): boolean {
  return deleteEntity(getStore().insights, id);
}

export function clearWorkspaceInsights(workspaceId: string): void {
  const insights = getInsightsByWorkspace(workspaceId);
  insights.forEach((i) => deleteInsight(i.id));
}

/**
 * Generate Stripe-style KPIs
 */
export function generateKPIs(workspaceId: string): DashboardKPI[] {
  const taskStats = getTaskStats(workspaceId);
  const crmSummary = getCrmSummary(workspaceId);
  const teamStats = getTeamStats(workspaceId);

  // Calculate completion rate
  const completionRate = taskStats.total > 0
    ? Math.round((taskStats.byStatus.done / taskStats.total) * 100)
    : 0;

  // Calculate conversion rate (leads to customers)
  const totalLeads = crmSummary.byStage.lead + crmSummary.byStage.qualified + crmSummary.byStage.proposal + crmSummary.byStage.negotiation + crmSummary.byStage.customer;
  const conversionRate = totalLeads > 0
    ? Math.round((crmSummary.byStage.customer / totalLeads) * 100)
    : 0;

  return [
    {
      id: 'kpi-tasks-active',
      label: 'Active Tasks',
      value: taskStats.total - taskStats.byStatus.done - taskStats.byStatus.cancelled,
      previousValue: taskStats.total - taskStats.byStatus.done - taskStats.byStatus.cancelled - taskStats.completedThisWeek,
      change: taskStats.completedThisWeek > 0 ? -Math.round((taskStats.completedThisWeek / (taskStats.total || 1)) * 100) : 0,
      trend: taskStats.completedThisWeek > 0 ? 'down' : 'stable',
      format: 'number',
      color: 'blue',
    },
    {
      id: 'kpi-completion-rate',
      label: 'Completion Rate',
      value: completionRate,
      change: 5,
      trend: 'up',
      format: 'percentage',
      color: 'green',
    },
    {
      id: 'kpi-pipeline-value',
      label: 'Pipeline Value',
      value: crmSummary.totalDealValue,
      change: 12,
      trend: 'up',
      format: 'currency',
      color: 'purple',
    },
    {
      id: 'kpi-new-leads',
      label: 'New Leads',
      value: crmSummary.newLeadsThisWeek,
      change: crmSummary.newLeadsThisWeek > 2 ? 15 : -5,
      trend: crmSummary.newLeadsThisWeek > 2 ? 'up' : 'down',
      format: 'number',
      color: 'orange',
    },
    {
      id: 'kpi-conversion-rate',
      label: 'Conversion Rate',
      value: conversionRate,
      change: 3,
      trend: conversionRate > 20 ? 'up' : 'stable',
      format: 'percentage',
      color: 'teal',
    },
    {
      id: 'kpi-team-members',
      label: 'Team Members',
      value: teamStats.active,
      format: 'number',
      trend: 'stable',
      color: 'indigo',
    },
  ];
}

/**
 * Generate automatic insights based on current workspace data
 */
export function generateWorkspaceInsights(workspaceId: string): Insight[] {
  // Clear existing insights first
  clearWorkspaceInsights(workspaceId);

  const insights: Insight[] = [];
  const tasks = getTasksByWorkspace(workspaceId);
  const overdueTasks = getOverdueTasks(workspaceId);
  const tasksDueToday = getTasksDueToday(workspaceId);

  // Overdue alert
  if (overdueTasks.length > 0) {
    insights.push(
      createInsight({
        workspaceId,
        type: 'alert',
        title: 'Overdue Tasks Alert',
        summary: `${overdueTasks.length} task${overdueTasks.length > 1 ? 's are' : ' is'} past due. Prioritize these to keep your workflow on track.`,
        data: { overdueCount: overdueTasks.length, taskIds: overdueTasks.map((t) => t.id) },
        priority: 'urgent',
        metric: 'overdue',
        value: overdueTasks.length,
        trend: 'up',
      })
    );
  }

  // Due today notification
  if (tasksDueToday.length > 0) {
    insights.push(
      createInsight({
        workspaceId,
        type: 'alert',
        title: 'Tasks Due Today',
        summary: `You have ${tasksDueToday.length} task${tasksDueToday.length > 1 ? 's' : ''} due today. Stay focused!`,
        data: { count: tasksDueToday.length },
        priority: 'action',
        metric: 'due_today',
        value: tasksDueToday.length,
      })
    );
  }

  // Task status summary
  const triageTasks = tasks.filter((t) => t.status === 'triage');
  const inProgressTasks = tasks.filter((t) => t.status === 'in_progress');
  const inReviewTasks = tasks.filter((t) => t.status === 'in_review');
  const doneTasks = tasks.filter((t) => t.status === 'done');

  insights.push(
    createInsight({
      workspaceId,
      type: 'summary',
      title: 'Task Pipeline Summary',
      summary: `${triageTasks.length} in triage, ${inProgressTasks.length} in progress, ${inReviewTasks.length} in review, ${doneTasks.length} completed`,
      data: {
        total: tasks.length,
        triage: triageTasks.length,
        inProgress: inProgressTasks.length,
        inReview: inReviewTasks.length,
        done: doneTasks.length,
      },
      priority: 'info',
    })
  );

  // Triage recommendation
  if (triageTasks.length > 3) {
    insights.push(
      createInsight({
        workspaceId,
        type: 'recommendation',
        title: 'Triage Backlog Growing',
        summary: `You have ${triageTasks.length} items in triage. Consider reviewing and prioritizing them.`,
        data: { triageCount: triageTasks.length },
        priority: 'action',
      })
    );
  }

  // CRM insights
  const contacts = getContactsByWorkspace(workspaceId);
  const contactStats = getContactStats(workspaceId);
  const totalDealValue = getTotalDealValue(workspaceId);

  if (contacts.length > 0) {
    insights.push(
      createInsight({
        workspaceId,
        type: 'summary',
        title: 'Sales Pipeline Overview',
        summary: `${contactStats.lead} leads, ${contactStats.qualified + contactStats.proposal} qualified, ${contactStats.negotiation} in negotiation, ${contactStats.customer} customers`,
        data: contactStats,
        priority: 'info',
        metric: 'pipeline',
        value: totalDealValue,
      })
    );

    // Lead conversion opportunity
    if (contactStats.lead > 3) {
      insights.push(
        createInsight({
          workspaceId,
          type: 'recommendation',
          title: 'Lead Conversion Opportunity',
          summary: `You have ${contactStats.lead} leads waiting to be qualified. Reach out to move them forward in the pipeline.`,
          data: { leadCount: contactStats.lead },
          priority: 'action',
        })
      );
    }

    // Deal value insight
    if (totalDealValue > 0) {
      insights.push(
        createInsight({
          workspaceId,
          type: 'kpi',
          title: 'Total Pipeline Value',
          summary: `Your current pipeline is worth ₩${totalDealValue.toLocaleString()}`,
          data: { totalValue: totalDealValue },
          priority: 'info',
          metric: 'deal_value',
          value: totalDealValue,
          trend: 'up',
          change: 12,
        })
      );
    }
  }

  // Notes insight
  const notes = getNotesByWorkspace(workspaceId);
  if (notes.length > 0) {
    insights.push(
      createInsight({
        workspaceId,
        type: 'summary',
        title: 'Knowledge Base Status',
        summary: `${notes.length} note${notes.length > 1 ? 's' : ''} documented. Keep building your knowledge base!`,
        data: { noteCount: notes.length },
        priority: 'info',
      })
    );
  }

  // Team insight
  const teamStats = getTeamStats(workspaceId);
  if (teamStats.total > 0) {
    insights.push(
      createInsight({
        workspaceId,
        type: 'summary',
        title: 'Team Overview',
        summary: `${teamStats.active} active members: ${teamStats.byRole.team_lead} leads, ${teamStats.byRole.developer} developers, ${teamStats.byRole.planner} planners, ${teamStats.byRole.qa} QA`,
        data: teamStats,
        priority: 'info',
      })
    );
  }

  return insights;
}

/**
 * Generate recent activity feed
 */
export function getRecentActivity(workspaceId: string, limit = 10): ActivityItem[] {
  const activities: ActivityItem[] = [];
  const tasks = getTasksByWorkspace(workspaceId);
  const contacts = getContactsByWorkspace(workspaceId);
  const notes = getNotesByWorkspace(workspaceId);

  // Add task activities
  const recentTasks = tasks
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 5);

  recentTasks.forEach((task) => {
    activities.push({
      id: `activity-task-${task.id}`,
      type: task.status === 'done' ? 'task_completed' : 'task_created',
      title: task.status === 'done' ? `Completed: ${task.title}` : `Task: ${task.title}`,
      description: task.identifier,
      timestamp: task.updatedAt,
      entityId: task.id,
      entityType: 'task',
    });
  });

  // Add contact activities
  const recentContacts = contacts
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 3);

  recentContacts.forEach((contact) => {
    activities.push({
      id: `activity-contact-${contact.id}`,
      type: contact.stage === 'customer' ? 'deal_won' : 'contact_added',
      title: contact.stage === 'customer' ? `New customer: ${contact.name}` : `Contact: ${contact.name}`,
      description: contact.company || contact.email,
      timestamp: contact.updatedAt,
      entityId: contact.id,
      entityType: 'contact',
    });
  });

  // Add note activities
  const recentNotes = notes
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 2);

  recentNotes.forEach((note) => {
    activities.push({
      id: `activity-note-${note.id}`,
      type: 'note_created',
      title: `Note: ${note.title}`,
      timestamp: note.updatedAt,
      entityId: note.id,
      entityType: 'note',
    });
  });

  // Sort all activities by timestamp and limit
  return activities
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, limit);
}

/**
 * Get comprehensive dashboard data (Stripe-style)
 */
export function getDashboardData(workspaceId: string): DashboardData {
  const taskStats = getTaskStats(workspaceId);
  const crmSummary = getCrmSummary(workspaceId);
  const teamStats = getTeamStats(workspaceId);

  return {
    kpis: generateKPIs(workspaceId),
    taskSummary: taskStats,
    crmSummary: crmSummary,
    teamSummary: teamStats,
    recentActivity: getRecentActivity(workspaceId),
  };
}

/**
 * Legacy function for backward compatibility
 */
export function getWorkspaceDashboard(workspaceId: string): {
  taskSummary: { total: number; todo: number; inProgress: number; done: number; overdue: number };
  contactSummary: Record<string, number>;
  noteCount: number;
  recentInsights: Insight[];
} {
  const taskStats = getTaskStats(workspaceId);
  const contactStats = getContactStats(workspaceId);
  const notes = getNotesByWorkspace(workspaceId);
  const insights = getInsightsByWorkspace(workspaceId).slice(-5);

  return {
    taskSummary: {
      total: taskStats.total,
      todo: taskStats.byStatus.triage + taskStats.byStatus.backlog,
      inProgress: taskStats.byStatus.in_progress + taskStats.byStatus.in_review,
      done: taskStats.byStatus.done,
      overdue: taskStats.overdue,
    },
    contactSummary: contactStats,
    noteCount: notes.length,
    recentInsights: insights,
  };
}
